import { useEffect, useState } from 'react';
import ButtonStartIcon from '../../../button/ButtonStartIcon';
import Typography from '@mui/material/Typography';
import Dialog from '../../../dialog/Dialog';
import useFeeds from '../../../../hooks/feed/useFeed';
import { Alert, Checkbox, FormControlLabel, Chip, CircularProgress } from '@mui/material';
import Form from '../Form/Form';
import Card from '../Card/Card';
import { addDays, parseISO } from 'date-fns';
import useFetchData from '../../../../hooks/useFetchData';

const computeExpireAt = (minDays, pusblishedAt) => {
  if (!minDays && minDays !== 0) return null;
  const days = parseInt(minDays, 10);
  if (isNaN(days)) return null;
  const start = pusblishedAt ? parseISO(pusblishedAt) : new Date();
  return addDays(start, days).toISOString();
};
const computeMinDaysFromExpire = (expireAt, pusblishedAt) => {
  if (!expireAt) return null;
  try {
    const exp = parseISO(expireAt);
    const start = pusblishedAt ? parseISO(pusblishedAt) : new Date();
    const diffMs = exp.getTime() - start.getTime();
    if (diffMs < 0) return -1; // indica inválido (expire antes que publish)
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return days;
  } catch (err) {
    return null;
  }
};
const FeedDialog = ({ show, onClose, type, data = {} }) => {
  const {
    errors: errorsData,
    createFeed,
    updateFeed,
    deleteFeed,
    archiveFeed,
    uploadAttachment,
    previewNotification,
    sendNotification
  } = useFeeds();
  const types = ['create', 'edit', 'delete', 'archive', 'preview', 'send-notification'];
  const yearLevelRoles = ['Teacher', 'Student', 'Parent'];
  const [form, setInputForm] = useState(data);
  const [errors, setInputErrors] = useState(errorsData);
  const [loading, toggleLoading] = useState(false);

  // Send notification state - fetch roles and years only when needed
  const { isLoading: isLoadingRoles, data: dataRoles = [] } = useFetchData('roles/standard');
  const { isLoading: isLoadingYears, data: dataYears = [] } = useFetchData('setting/year');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedYearLevels, setSelectedYearLevels] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const yearLevelRolesNotification = ['Teacher', 'Student', 'Parent'];
  const hasYearLevelRoleSelected = selectedRoles.some((r) => yearLevelRolesNotification.includes(r));

  const onChange = (name, value) => {
    if (name === 'minDays') {
      const parsed = value === '' ? null : parseInt(value, 10);
      const expireAt = computeExpireAt(parsed, form.pusblishedAt);
      setInputForm({ ...form, minDays: parsed, expireAt });
      setInputErrors({ ...errors, minDays: null });
    } else if (name === 'pusblishedAt') {
      // value expected as ISO string
      const expireAt = computeExpireAt(form.minDays, value);
      setInputForm({ ...form, pusblishedAt: value, expireAt });
      setInputErrors({ ...errors, pusblishedAt: null });
    } else if (name === 'expireAt') {
      // usuario cambió expireAt -> recalcular minDays
      const minFromExpire = computeMinDaysFromExpire(value, form.pusblishedAt);
      if (minFromExpire === -1) {
        // expire antes que published -> marcar error
        setInputForm({ ...form, expireAt: value, minDays: null });
        setInputErrors({ ...errors, expireAt: 'Expire date must be after publish date' });
      } else {
        setInputForm({ ...form, expireAt: value, minDays: minFromExpire });
        setInputErrors({ ...errors, expireAt: null, minDays: null });
      }
    } else {
      setInputForm({ ...form, [name]: value });
      setInputErrors({ ...errors, [name]: null });
    }
  };

  const onPreviewRecipients = async () => {
    if (selectedRoles.length === 0) {
      setInputErrors({ message: 'Select at least one role' });
      return;
    }
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const result = await previewNotification(form._id, selectedRoles, selectedYearLevels);
      if (result.error) {
        setInputErrors({ message: result.message });
      } else {
        setPreviewData(result.data);
        setInputErrors({});
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const onSendNotification = async () => {
    if (selectedRoles.length === 0) {
      setInputErrors({ message: 'Select at least one role' });
      return;
    }
    toggleLoading(true);
    setSendResult(null);
    try {
      const result = await sendNotification(form._id, selectedRoles, selectedYearLevels);
      if (result.error) {
        setInputErrors({ message: result.message });
      } else {
        setSendResult(result.data);
        setInputErrors({});
      }
    } finally {
      toggleLoading(false);
    }
  };

  const onSave = async (e) => {
    if (!types.includes(type)) {
      setInputErrors({ message: 'Invalid type' });
      return;
    }

    e.preventDefault();
    toggleLoading(true);
    try {
      let result = { error: false };
      if (['edit', 'create'].includes(type)) {
        const resultUpload = await uploadAttachment(form);
        if (resultUpload.error) {
          setInputErrors({ attachment: resultUpload.details });
          toggleLoading(false);
          return;
        }

        const newMedia = resultUpload.data || [];
        form.attachment = form.attachment
          ?.map((item) => {
            if (item.type === 'file') {
              const find = newMedia?.find((attachment) => attachment.id === item.id);
              if (find) {
                return { ...item, ...find };
              }
            }
            return item;
          })
          .filter((item) => item.link);
      }

      // Check if year-level roles are selected but no year levels chosen
      const currentHasYearLevelRole = form?.viewRoles?.some((role) =>
        yearLevelRoles.some((yrRole) => role?.name?.toLowerCase() === yrRole.toLowerCase())
      );

      if (type === 'create') {
        if (!form?.postType) {
          setInputErrors({ ...errors, postType: 'Post type is required' });
          toggleLoading(false);
          return;
        }

        if (currentHasYearLevelRole && (!form?.viewYearLevels || form.viewYearLevels.length === 0)) {
          setInputErrors({
            ...errors,
            viewYearLevels:
              'At least one year level must be selected when Teacher, Student, or Parent roles are chosen'
          });
          toggleLoading(false);
          return;
        }

        result = await createFeed(form);
      }
      if (type === 'edit') {
        if (!form?.postType) {
          setInputErrors({ ...errors, postType: 'Post type is required' });
          toggleLoading(false);
          return;
        }

        if (currentHasYearLevelRole && (!form?.viewYearLevels || form.viewYearLevels.length === 0)) {
          setInputErrors({
            ...errors,
            viewYearLevels:
              'At least one year level must be selected when Teacher, Student, or Parent roles are chosen'
          });
          toggleLoading(false);
          return;
        }

        result = await updateFeed(form._id, form);
      }
      if (type === 'delete') {
        result = await deleteFeed(form._id);
      }
      if (type === 'archive') {
        result = await archiveFeed(form._id);
      }
      if (result.error) {
        setInputErrors(result);
      } else {
        const res = ['create', 'edit'].includes(type) ? { ...form, ...result.data } : form;
        onClose({ changed: true, action: type, feed: { ...res } });
      }
    } finally {
      toggleLoading(false);
    }
  };

  useEffect(() => {
    setInputErrors(errorsData);
  }, [errorsData]);

  useEffect(() => {
    setInputForm(data || {});
  }, [data]);

  const NotificationActions = (
    <>
      <ButtonStartIcon
        onClick={onClose}
        color='primary'
        variant='outlined'
        disabled={loading}
        startIcon={['fal', 'times']}
      >
        Close
      </ButtonStartIcon>
      <ButtonStartIcon
        onClick={onPreviewRecipients}
        color='primary'
        variant='outlined'
        disabled={loading || previewLoading || selectedRoles.length === 0}
        loading={previewLoading}
        startIcon={['fal', 'eye']}
      >
        Preview
      </ButtonStartIcon>
      {previewData && !previewData.exceedsLimit && previewData.totalRecipients > 0 && !sendResult && (
        <ButtonStartIcon
          onClick={onSendNotification}
          color='primary'
          variant='contained'
          disabled={loading}
          loading={loading}
          startIcon={['fal', 'paper-plane']}
        >
          Send ({previewData.totalRecipients})
        </ButtonStartIcon>
      )}
    </>
  );

  const Actions = (
    <>
      <ButtonStartIcon
        onClick={onClose}
        color='primary'
        variant='outlined'
        disabled={loading}
        startIcon={['fal', 'times']}
      >
        Cancel
      </ButtonStartIcon>
      {['create', 'archive', 'edit', 'delete'].includes(type) && (
        <ButtonStartIcon
          onClick={onSave}
          color='primary'
          variant='contained'
          disabled={loading}
          loading={loading}
          startIcon={['fal', 'check']}
        >
          {type === 'delete' ? 'Confirm' : 'Save'}
        </ButtonStartIcon>
      )}
    </>
  );

  const getDialogActions = () => {
    if (type === 'preview') return null;
    if (type === 'send-notification') return NotificationActions;
    return Actions;
  };

  return (
    <Dialog
      maxWidth={['archive', 'delete'].includes(type) ? 'xs' : type === 'send-notification' ? 'sm' : 'md'}
      id={`${type}-feed`}
      show={show}
      title={type === 'send-notification' ? `Send Email Notification` : `${type} feed`}
      onClose={onClose}
      actions={getDialogActions()}
      onCloseHeader={onClose}
    >
      {['archive', 'delete'].includes(type) ? (
        <Typography>
          Are you sure you want to {type} the feed: <b>{form.title}</b>?
        </Typography>
      ) : type === 'preview' ? (
        <Card feed={form} isPreview={true} />
      ) : type === 'send-notification' ? (
        <div className='flex flex-col gap-4'>
          <Alert severity='info' variant='outlined'>
            Send email notification for: <b>{form.title}</b>
          </Alert>

          {/* Role Selection */}
          <div>
            <Typography variant='subtitle2' color='primary' className='mb-2'>
              Select Roles to Notify:
            </Typography>
            {isLoadingRoles ? (
              <CircularProgress size={20} />
            ) : (
              <div className='grid grid-cols-3 gap-1'>
                {dataRoles?.map((role) => (
                  <FormControlLabel
                    key={role._id}
                    control={
                      <Checkbox
                        size='small'
                        checked={selectedRoles.includes(role.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRoles([...selectedRoles, role.id]);
                          } else {
                            setSelectedRoles(selectedRoles.filter((r) => r !== role.id));
                            // Clear year levels if no year-level role remains
                            const remaining = selectedRoles.filter((r) => r !== role.id);
                            if (!remaining.some((r) => yearLevelRolesNotification.includes(r))) {
                              setSelectedYearLevels([]);
                            }
                          }
                          setPreviewData(null);
                          setSendResult(null);
                        }}
                        color='primary'
                      />
                    }
                    label={<Typography variant='body2'>{role.name}</Typography>}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Year Level Selection */}
          {hasYearLevelRoleSelected && (
            <div>
              <Typography variant='subtitle2' color='primary' className='mb-2'>
                Filter by Year Levels (optional):
              </Typography>
              {isLoadingYears ? (
                <CircularProgress size={20} />
              ) : (
                <div className='flex flex-wrap gap-1'>
                  {dataYears?.map((year) => (
                    <Chip
                      key={year._id}
                      label={year.name}
                      size='small'
                      variant={selectedYearLevels.includes(year.name) ? 'filled' : 'outlined'}
                      color={selectedYearLevels.includes(year.name) ? 'primary' : 'default'}
                      onClick={() => {
                        if (selectedYearLevels.includes(year.name)) {
                          setSelectedYearLevels(selectedYearLevels.filter((y) => y !== year.name));
                        } else {
                          setSelectedYearLevels([...selectedYearLevels, year.name]);
                        }
                        setPreviewData(null);
                        setSendResult(null);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Preview Result */}
          {previewData && (
            <div className='flex flex-col gap-2'>
              {previewData.exceedsLimit ? (
                <Alert severity='warning'>
                  Too many recipients ({previewData.totalRecipients}). Maximum allowed is{' '}
                  {previewData.maxRecipients}. Please narrow your selection.
                </Alert>
              ) : previewData.totalRecipients === 0 ? (
                <Alert severity='info'>
                  No new recipients to notify.
                  {previewData.alreadySent > 0 &&
                    ` ${previewData.alreadySent} user(s) were already notified.`}
                </Alert>
              ) : (
                <Alert severity='success'>
                  Ready to send to <b>{previewData.totalRecipients}</b> user(s).
                  {previewData.alreadySent > 0 &&
                    ` ${previewData.alreadySent} user(s) were already notified and will be skipped.`}
                </Alert>
              )}
            </div>
          )}

          {/* Send Result */}
          {sendResult && (
            <Alert severity='success'>
              Notification sent to <b>{sendResult.emailsSent}</b> user(s).
              {sendResult.failed > 0 && ` ${sendResult.failed} failed.`}
            </Alert>
          )}
        </div>
      ) : (
        <Form form={form} errors={errors} onChange={onChange} />
      )}
      {errors && errors.message && (
        <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
          {errors.message}
        </Alert>
      )}
    </Dialog>
  );
};

export default FeedDialog;
